'use client';

import { useState, useEffect } from "react";
import CustomModal from "../modal/modal";
import CustomButton from "../buttons/button";
import classes from "./email_verification_modal.module.css";
import SafeImage from "../safe_image";
import mailbox from '@/assets/png/mailbox.png';
import Mail from '@/assets/svg/icons/mail.svg';
import CustomSVG from "../svg";

/**
 * Email Verification Modal
 * 
 * Megjelenik közepes reCAPTCHA score (0.3 - 0.7) esetén.
 * Kéri a felhasználót, hogy erősítse meg az email címét.
 * 
 * @param {boolean} isOpen - Modal megjelenítése
 * @param {function} onClose - Bezárás callback
 * @param {string} email - Elküldött email cím
 * @param {number} score - reCAPTCHA score (debug célra)
 * @param {function} onResend - Újraküldés callback
 * @param {boolean} isResending - Töltés állapot
 */
export default function EmailVerificationModal({ 
    isOpen = false, 
    onClose, 
    email = "", 
    score = null,
    onResend = null,
    isResending = false 
}) {
    const [countdown, setCountdown] = useState(60); // 60 másodperc
    const [canResend, setCanResend] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setCountdown(60);
            setCanResend(false);
            return;
        }

        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    setCanResend(true);
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [isOpen]);

    const handleResend = () => {
        if (canResend && onResend && !isResending) {
            setCountdown(60);
            setCanResend(false);
            onResend();
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <CustomModal isOpen={isOpen} onClose={onClose} maxWidth="500px">
            <div className={classes["verification-modal"]}>
                <div className={classes["modal-header"]}>
                    <div className={classes["icon-wrapper"]}>
                        <SafeImage 
                            src={mailbox} 
                            alt="Mailbox" 
                            width={80} 
                            height={80}
                            className={classes["mailbox-image"]}
                        />
                    </div>
                    <h2 className={classes["modal-title"]}>
                        Email megerősítés szükséges
                    </h2>
                </div>

                <div className={classes["modal-body"]}>
                    <div className={classes["info-box"]}>
                        <CustomSVG svg={Mail} className={classes["mail-icon"]} />
                        <div className={classes["info-text"]}>
                            <p className="margin-0 text-md-semibold color-gray-900">
                                Elküldtük a megerősítő emailt
                            </p>
                            <p className="margin-0 text-sm-regular color-gray-600">
                                {email}
                            </p>
                        </div>
                    </div>

                    <div className={classes["description"]}>
                        <p className="margin-0 text-md-regular color-gray-700">
                            Biztonsági okokból kérjük, erősítsd meg az email címedet. 
                            Kattints a leveledben található linkre a folytatáshoz.
                        </p>
                    </div>

                    {score !== null && process.env.NEXT_PUBLIC_TEST_RECAPTCHA_SCORE && (
                        <div className={classes["debug-info"]}>
                            <p className="margin-0 text-xs-regular color-gray-500">
                                🔍 Debug: reCAPTCHA score = {score}
                            </p>
                        </div>
                    )}

                    <div className={classes["actions"]}>
                        <div className={classes["resend-section"]}>
                            <p className="margin-0 text-sm-regular color-gray-600">
                                Nem kaptad meg az emailt?
                            </p>
                            {!canResend ? (
                                <p className="margin-0 text-sm-semibold color-gray-500">
                                    Újraküldés {formatTime(countdown)} múlva
                                </p>
                            ) : (
                                <button
                                    onClick={handleResend}
                                    disabled={isResending}
                                    className={classes["resend-button"]}
                                >
                                    {isResending ? 'Küldés...' : 'Újraküldés'}
                                </button>
                            )}
                        </div>

                        <CustomButton
                            type="button"
                            onClick={onClose}
                            variant="secondary"
                            size="large"
                            fullWidth
                        >
                            Értem, köszönöm
                        </CustomButton>
                    </div>

                    <div className={classes["footer-note"]}>
                        <p className="margin-0 text-xs-regular color-gray-500 text-center">
                            💡 Tipp: Ellenőrizd a spam mappát is!
                        </p>
                    </div>
                </div>
            </div>
        </CustomModal>
    );
}
